import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';
import Card from '../../components/ui/Card';
import MachineFilters from '../../components/ui/MachineFilters';
import PageHeader from '../../components/ui/PageHeader';
import { useMachineFilters } from '../../hooks/useMachineFilters';
import { insightsApi, type ComposterInsightRow } from '../../services/insightsApi';

const InsightsView = () => {
  const f = useMachineFilters({ machineType: 'composter' });
  const q = useQuery({
    queryKey: ['insights', 'composter', f.selectedMachineId, f.from, f.to],
    enabled: Boolean(f.selectedMachineId),
    queryFn: async () =>
      (await insightsApi.composter(f.selectedMachineId as string, f.from, f.to)).data,
  });

  const rows = q.data?.rows ?? [];

  return (
    <>
      <PageHeader
        title="Composter Insights"
        subtitle="Detailed throughput and efficiency breakdown."
        actions={
          <MachineFilters
            machines={f.machines}
            selectedMachineId={f.selectedMachineId}
            onMachineChange={f.setMachineId}
            from={f.from}
            to={f.to}
            onFromChange={f.setFrom}
            onToChange={f.setTo}
            loading={f.machinesLoading}
          />
        }
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card title="Totes loaded per day">
          <ReactECharts option={totesOption(rows)} style={{ height: 280 }} notMerge lazyUpdate />
        </Card>
        <Card title="Loading vs. offloading time (minutes)">
          <ReactECharts
            option={timeBreakdownOption(rows)}
            style={{ height: 280 }}
            notMerge
            lazyUpdate
          />
        </Card>
        <Card title="Weight reduction trend">
          <ReactECharts
            option={weightReducedOption(rows)}
            style={{ height: 280 }}
            notMerge
            lazyUpdate
          />
        </Card>
        <Card title="Throughput vs. weight (correlation)">
          <ReactECharts
            option={scatterOption(rows)}
            style={{ height: 280 }}
            notMerge
            lazyUpdate
          />
        </Card>
      </div>

      <Card className="mt-6" title={`Daily breakdown · ${rows.length} rows`}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium">Totes</th>
                <th className="py-2 pr-4 font-medium">Kgs loaded</th>
                <th className="py-2 pr-4 font-medium">Kgs offloaded</th>
                <th className="py-2 pr-4 font-medium">Reduction</th>
                <th className="py-2 pr-4 font-medium">Load (min)</th>
                <th className="py-2 pr-4 font-medium">Offload (min)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {rows.map((r) => (
                <tr key={r.ts} className="hover:bg-slate-50">
                  <td className="py-2 pr-4 font-mono text-xs text-slate-500">{r.ts}</td>
                  <td className="py-2 pr-4">{r.totesLoaded}</td>
                  <td className="py-2 pr-4">{r.kgsLoaded.toFixed(0)}</td>
                  <td className="py-2 pr-4">{r.kgsOffloaded.toFixed(0)}</td>
                  <td className="py-2 pr-4 text-emerald-600">
                    -{r.weightReduced.toFixed(0)} kg
                  </td>
                  <td className="py-2 pr-4">{(r.timeLoading / 60).toFixed(0)}</td>
                  <td className="py-2 pr-4">{(r.timeOffloading / 60).toFixed(0)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-xs text-slate-400">
                    No data in window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
};

const totesOption = (rows: ComposterInsightRow[]) => ({
  grid: { left: 50, right: 20, top: 20, bottom: 30 },
  tooltip: { trigger: 'axis' as const },
  xAxis: {
    type: 'category' as const,
    data: rows.map((r) => r.ts.slice(5)),
    axisLine: { lineStyle: { color: '#e2e8f0' } },
    axisLabel: { color: '#64748b' },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { color: '#64748b' },
    splitLine: { lineStyle: { color: '#e2e8f0' } },
  },
  series: [
    {
      type: 'bar' as const,
      data: rows.map((r) => r.totesLoaded),
      itemStyle: { color: '#2d5f3f', borderRadius: [4, 4, 0, 0] },
    },
  ],
});

const timeBreakdownOption = (rows: ComposterInsightRow[]) => ({
  grid: { left: 50, right: 20, top: 30, bottom: 30 },
  tooltip: { trigger: 'axis' as const },
  legend: { data: ['load', 'offload'], top: 0, textStyle: { color: '#475569' } },
  xAxis: {
    type: 'category' as const,
    data: rows.map((r) => r.ts.slice(5)),
    axisLabel: { color: '#64748b' },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { color: '#64748b', formatter: '{value}m' },
    splitLine: { lineStyle: { color: '#e2e8f0' } },
  },
  series: [
    {
      name: 'load',
      type: 'bar' as const,
      stack: 'time',
      data: rows.map((r) => Math.round(r.timeLoading / 60)),
      itemStyle: { color: '#0ea5e9' },
    },
    {
      name: 'offload',
      type: 'bar' as const,
      stack: 'time',
      data: rows.map((r) => Math.round(r.timeOffloading / 60)),
      itemStyle: { color: '#8b5cf6' },
    },
  ],
});

const weightReducedOption = (rows: ComposterInsightRow[]) => ({
  grid: { left: 50, right: 20, top: 20, bottom: 30 },
  tooltip: { trigger: 'axis' as const },
  xAxis: {
    type: 'category' as const,
    data: rows.map((r) => r.ts.slice(5)),
    axisLabel: { color: '#64748b' },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { color: '#64748b', formatter: '{value} kg' },
    splitLine: { lineStyle: { color: '#e2e8f0' } },
  },
  series: [
    {
      type: 'line' as const,
      smooth: true,
      data: rows.map((r) => r.weightReduced),
      itemStyle: { color: '#10b981' },
      areaStyle: { color: 'rgba(16, 185, 129, 0.15)' },
    },
  ],
});

const scatterOption = (rows: ComposterInsightRow[]) => ({
  grid: { left: 50, right: 20, top: 20, bottom: 40 },
  tooltip: {
    trigger: 'item' as const,
    formatter: (p: { value: [number, number] }) =>
      `${p.value[0]} totes → ${p.value[1].toFixed(0)} kg`,
  },
  xAxis: {
    type: 'value' as const,
    name: 'Totes',
    nameTextStyle: { color: '#64748b' },
    axisLabel: { color: '#64748b' },
  },
  yAxis: {
    type: 'value' as const,
    name: 'Kgs loaded',
    nameTextStyle: { color: '#64748b' },
    axisLabel: { color: '#64748b' },
    splitLine: { lineStyle: { color: '#e2e8f0' } },
  },
  series: [
    {
      type: 'scatter' as const,
      symbolSize: 10,
      data: rows.map((r) => [r.totesLoaded, r.kgsLoaded]),
      itemStyle: { color: '#9bc53d' },
    },
  ],
});

export default InsightsView;
